import React from "react";
import { PlusIcon, MinusIcon, PencilSquareIcon, TrashIcon, SparklesIcon } from "@heroicons/react/24/outline";
import moment from "moment";

const EtReflectionCardMobile = ({
  refItem,
  index,
  showDetail,
  toggleShowDetail,
  handleEdit,
  handleDelete,
  createMarkup,
  mode="full",        // 24 Jan 2026
  showOwner=false,    // 06 Feb 2026
}) => {

    // First Created - 24 Feb 2026
    // Author  - Atul Pandey
    // Purpose - Show detailed performance input controls for a single habit (toggle, time, note, log button)
    // Inherited from EtReflectionCard and refined
    // Updated - 24 Feb 2026 - refined JSX 
    
    // Original Comp Update Trail
    // ----------------------------------------
    // First Created - 03 Dec 2025
    // Author - Atul Pandey
    // Updated - 24 Jan 2026 - to display content based on mode
    // Updated - 06 Feb 2026 - display username initial for "Everyone tab" based on showOwner flag


  // 1 - Reflection item  
  const {
    reflectionNoteText,
    reflectionDate,
    reflectionUpdateDate,
    parentReflectionId,
    trailId,
    sessionId,
    userMagicMomentId
  } = refItem;

  return (
  <div className="bg-gray-100 rounded-lg border shadow-sm p-2">

    {/* -------------------------------- */}
    {/* Row 1: Left (toggle + index + owner) | Right (badges + actions) */}
    {/* -------------------------------- */}
    <div className="flex justify-between items-start gap-2">

      {/* Left group */}
      <div className="flex items-start gap-2 min-w-0">

        {/* Expand toggle */}
        <button
          type="button"
          onClick={() => toggleShowDetail(refItem._id)}
          className="rounded-full h-7 w-7 bg-white border flex justify-center items-center shrink-0"
          aria-label={showDetail?.[refItem._id] ? "Collapse" : "Expand"}
        >
          {showDetail?.[refItem._id] ? (
            <MinusIcon className="h-4 w-4 text-orange-600" />
          ) : (
            <PlusIcon className="h-4 w-4 text-orange-600" />
          )}
        </button>

        {/* Index + header + preview */}
        <div className="min-w-0">

          {/* Index + updated time + owner */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-gray-600">
              {index + 1}.
            </p>

            {/* 06 Feb 2026 - Owner (Collective → Everyone) */}
            {showOwner && refItem?.userId?._id && (
              <div className="flex items-center gap-2 bg-white rounded-full px-2 py-[2px] border">
                <div className="h-5 w-5 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-700">
                  {(refItem.userId.name || refItem.userId.username || "U")[0]}
                </div>
                <p className="text-[10px] font-semibold text-gray-700">
                  {(refItem.userId.name || refItem.userId.username || "User").split(" ")[0]}
                </p>
              </div>
            )}

            {/* Updated time */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] rounded-full px-2 py-[1px] bg-lime-200 text-gray-800">
                {moment(reflectionUpdateDate).fromNow()}
              </span>
              <span className="text-[8px] italic text-gray-400">
                updated
              </span>
            </div>
          </div>

          {/* Preview (mobile friendly) */}
          <div className="mt-1 min-w-0">
            {mode === "full" ? (
              <div
                className="text-[11px] text-gray-700 line-clamp-2 break-words"
                dangerouslySetInnerHTML={createMarkup(reflectionNoteText)}
              />
            ) : (
              <p className="text-[11px] text-orange-600 font-semibold">
                Shared privately
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Right group */}
      <div className="flex items-center gap-2 shrink-0">

        {/* Magic Moment */}
        {userMagicMomentId && (
          <div className="flex items-center gap-1 bg-yellow-200 px-2 py-[2px] rounded-full shadow">
            <SparklesIcon className="h-4 w-4 text-yellow-700" />
            <span className="text-[9px] text-yellow-800 whitespace-nowrap">
              Magic
            </span>
          </div>
        )}

        {/* Actions */}
        {mode === "full" && (
          <>
            <button
              type="button"
              onClick={() => handleEdit(refItem)}
              className="p-1 rounded-full hover:bg-white"
              aria-label="Edit reflection"
            >
              <PencilSquareIcon className="h-5 w-5 text-orange-600" />
            </button>

            {!userMagicMomentId && (
              <button
                type="button"
                onClick={() => handleDelete(refItem)}
                className="p-1 rounded-full hover:bg-white"
                aria-label="Delete reflection"
              >
                <TrashIcon className="h-5 w-5 text-orange-600" />
              </button>
            )}
          </>
        )}
      </div>
    </div>

    {/* -------------------------------- */}
    {/* Expanded content */}
    {/* -------------------------------- */}
    {showDetail?.[refItem._id] && (
      <div className="mt-2">
        {mode === "full" ? (
          <div
            className="p-2 rounded bg-white text-[11px] text-gray-700 fix-nested-lists break-words"
            dangerouslySetInnerHTML={createMarkup(reflectionNoteText)}
          />
        ) : (
          <div className="p-2 rounded bg-white text-[11px] text-gray-700">
            <p className="text-orange-600 text-xs font-semibold">
              Reflection has been kept private by the user
            </p>
          </div>
        )}
      </div>
    )}

    {/* -------------------------------- */}
    {/* Metadata (compact, stacked) */}
    {/* -------------------------------- */}
    <div className="mt-2 bg-orange-50 rounded p-2">

      {/* Created */}
      <p className="text-[10px] text-gray-600">
        <span className="font-bold text-gray-700">Created:</span>{" "}
        {moment(reflectionDate).format("DD MMM YYYY, hh:mm A")}
      </p>

      {/* Week */}
      {parentReflectionId?.weekStarting && (
        <p className="text-[10px] text-gray-600 mt-1">
          <span className="font-bold text-gray-700">Week:</span>{" "}
          {moment(parentReflectionId.weekStarting).format("DD MMM")} →{" "}
          {moment(parentReflectionId.weekEnding).format("DD MMM")}
        </p>
      )}

      {/* Trail + Session */}
      {(trailId || sessionId) && (
        <div className="mt-1 space-y-1">
          {trailId && (
            <p className="text-[10px] text-gray-600">
              <span className="font-bold text-gray-700">Trail:</span>{" "}
              <span className="break-words">{trailId.trailName}</span>
            </p>
          )}

          {sessionId && (
            <p className="text-[10px] text-gray-600">
              <span className="font-bold text-gray-700">Session:</span>{" "}
              <span className="break-words">{sessionId.sessionName}</span>
            </p>
          )}
        </div>
      )}
    </div>
  </div>
);
};

export default EtReflectionCardMobile;